import { useTranslation } from 'react-i18next'
import type { UnitEconomics as UnitData } from '@/lib/api/amazon-listing'
import { SectionCard } from './shared/SectionCard'

interface UnitEconomicsProps {
  data: UnitData
}

function ConfidenceBadge({ confidence }: { confidence: number }) {
  const { t } = useTranslation()
  const level = confidence >= 80 ? 'high' : confidence >= 50 ? 'medium' : 'low'
  const cls = level === 'high'
    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
    : level === 'medium'
      ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
  const label = level === 'high'
    ? t('amazonListing.report.comparabilityHigh')
    : level === 'medium'
      ? t('amazonListing.report.comparabilityMedium')
      : t('amazonListing.report.comparabilityLow')

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {t('amazonListing.report.comparability')}: {label} ({confidence}%)
    </span>
  )
}

export function UnitEconomics({ data }: UnitEconomicsProps) {
  const { t } = useTranslation()

  return (
    <SectionCard title={t('amazonListing.report.unitEconomics')}>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <ConfidenceBadge confidence={data.comparability_confidence} />
        </div>
        <div className="rounded-lg bg-neutral-50 dark:bg-neutral-700/50 p-3">
          <div className="text-xs text-neutral-500 mb-1">{t('amazonListing.report.unitPrice')}</div>
          <div className="text-lg font-bold text-neutral-900 dark:text-white">
            {data.source_unit_price} / {data.source_unit}
          </div>
        </div>
        {data.competitor_unit_prices.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-neutral-500 border-b border-neutral-200 dark:border-neutral-700">
                <th className="text-left py-1.5 font-medium">ASIN</th>
                <th className="text-left py-1.5 font-medium">{data.source_unit}</th>
                <th className="text-right py-1.5 font-medium">{t('amazonListing.report.unitPrice')}</th>
              </tr>
            </thead>
            <tbody>
              {data.competitor_unit_prices.map((c) => (
                <tr key={c.asin} className="border-b border-neutral-100 dark:border-neutral-700/50">
                  <td className="py-1.5 font-mono text-xs">{c.asin}</td>
                  <td className="py-1.5">{c.unit}</td>
                  <td className="py-1.5 text-right font-medium">{c.unit_price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data.market_standard_size && (
          <p className="text-xs text-neutral-500">
            {t('amazonListing.report.marketStandardSize')}: {data.market_standard_size}
          </p>
        )}
      </div>
    </SectionCard>
  )
}
